import { Icons } from '@/utils/Icons';
import toast from 'react-hot-toast';
import React, { useEffect, useState } from 'react';
import MyBets from './MyBets';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { toggleEditStake } from '@/store';
import { useBetPlaceMutation, useGetStakesQuery } from '@/store/api/betApi';
import { PlaceBetPayload } from '@/types/Bet';
import { clearBet, setStakeAmount } from '@/store/slices/betSlice';
import { formatNumber } from '@/utils';
import ComponentLoader from './common/ComponentLoader';

const BettingContainer = () => {
  return (
    <div className="sticky top-0 max-xl:hidden text-black w-[350px] 2xl:w-[400px] 3xl:w-[450px] pl-[2px] min-h-[calc(100vh-135px)] max-h-[calc(100vh-135px)] overflow-x-hidden overflow-y-auto ml-0">
      <BetSlip />
    </div>
  );
};

export default BettingContainer;

function BetSlip() {
  const [betPlace, { data, isLoading: IsBetPlacing }] = useBetPlaceMutation();
  const [openTv, setOpenTv] = useState<boolean>(false);
  const [acceptOddsChange, setAcceptOddsChange] = useState<boolean>(false);
  const { data: stakeData, refetch } = useGetStakesQuery();
  const { selectedBet, stake } = useAppSelector((state) => state.bet);
  const betDetails = useAppSelector((state) => state.bet);
  const { liveTvUrl } = useAppSelector((state) => state.media);
  const profit =
    selectedBet?.betOn == 'BACK' ? formatNumber((Number(selectedBet?.rate) - 1) * stake) : stake;

  const bookmakerProfit =
    selectedBet?.betOn == 'BACK' ? formatNumber((Number(selectedBet?.rate) * stake) / 100) : stake;

  const addStake = (value: number) => {
    dispatch(setStakeAmount(stake + value));
  };

  const reset = () => {
    dispatch(clearBet());
  };
  const dispatch = useAppDispatch();

  const handleBetPlace = async () => {
    if (!selectedBet) return;

    const {
      eventId,
      etid,
      marketId,
      marketName,
      marketCategory,
      marketType,
      selectionId,
      runnerName,
      betOn,
      rate,
      fancyPercentage,
    } = selectedBet;

    if (!eventId || !marketId || !marketName || !selectionId || !runnerName || !rate || !betOn) {
      return;
    }

    const payload: PlaceBetPayload = {
      eventId,
      // Only present for Diamond markets; app-feed bets must not carry it.
      ...(etid ? { etid } : {}),
      marketId,
      marketName,
      marketCategory: marketCategory!,
      marketType: marketType!,
      selectionId,
      runnerName,
      betOn: betOn,
      stake: stake,
      rate,
      fancyPercentage,
      acceptOddsChange,
    };
    try {
      await betPlace(payload).unwrap();
      dispatch(clearBet());
      toast.success('Bet placed');
    } catch (error: unknown) {
      // Without this the slip just sits there on a rejection — no message, no
      // cleared slip — which reads as "nothing happened" even though the
      // backend gave a reason (rate out of range, market closed, ...).
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        'Could not place bet. Please try again.';
      toast.error(String(message));
    }
  };

  const handleStake = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setStakeAmount(Number(value)));
  };

  return (
    <div className="text-sm font-sans overflow-y-auto">
      {/* Live Match title */}
      <div
        onClick={() => setOpenTv((prev) => !prev)}
        className="flex items-center justify-between cursor-pointer bg-black text-white px-2 py-1 border-t border-gray-700 font-bold"
      >
        <span>Live Match</span>
        <Icons.Tv size={18} />
      </div>
      {openTv && liveTvUrl && (
        <div className="w-full aspect-video overflow-hidden">
          {' '}
          <iframe
            key={liveTvUrl}
            allowFullScreen={true}
            scrolling="no"
            className="w-full h-full"
            src={liveTvUrl}
          ></iframe>
        </div>
      )}
      {/* Place Bet title */}
      {selectedBet && (
        <>
          <div className="bg-black text-white px-2 py-1 border-t border-gray-700 mt-1 font-bold">
            Place Bet
          </div>
          {/* Bet Box */}
          <div
            className={`relative ${selectedBet?.betOn?.toLowerCase() == 'back' ? 'bg-back' : 'bg-lay'}`}
          >
            {/* Table header */}
            <div className="grid grid-cols-4 bg-gray-300 text-black font-bold px-2 py-1">
              <span>(Bet for)</span>
              <span className="text-center">Odds</span>
              <span className="text-center">Stake</span>
              <span className="text-right">Profit</span>
            </div>

            {/* Bet Row */}
            <div className="flex items-center px-2 py-1">
              <span className="font-bold w-[calc(100%-230px)]">{selectedBet?.runnerName}</span>
              <div className="flex w-[60px]">
                <input
                  value={selectedBet?.rate ? selectedBet?.rate : ''}
                  readOnly
                  className="h-6 w-full text-center bg-white border"
                />
                <div className="bg-[#cccccc]">
                  <div className="h-3 w-5 flex items-center justify-center">
                    <Icons.Up className="text-xs" />
                  </div>
                  <div className="h-3 w-5 flex items-center justify-center">
                    <Icons.Down className="text-xs" />
                  </div>
                </div>
              </div>
              <input
                type="number"
                value={stake ? stake : ''}
                onChange={handleStake}
                inputMode="numeric"
                onKeyDown={(e) => {
                  if (['e', 'E', '.', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                className="ml-[10px] w-20 h-6 text-center bg-white border appearance-none"
              />
              <span className="w-20 text-right font-bold">
                {selectedBet?.marketType?.toLowerCase() == 'fancy'
                  ? 0
                  : selectedBet?.marketCategory?.toLowerCase() == 'bookmaker'
                    ? bookmakerProfit
                    : profit}
              </span>
            </div>

            {/* Chips */}
            <div className="flex p-1 gap-[2px] justify-start flex-wrap mt-2">
              {stakeData?.stakeSet?.map(({ value, label }, index) => (
                <button
                  key={index}
                  onClick={() => addStake(value)}
                  className="w-[calc(20%-2px)] bg-gray-300 p-[5px] text-sm font-bold hover:bg-gray-400"
                >
                  +{label}
                </button>
              ))}
            </div>
            {/* Actions */}
            <div className="flex justify-between">
              {/* Clear */}
              <button
                onClick={() => dispatch(setStakeAmount(0))}
                className="text-base underline text-gray-800 min-w-20 w-[25%] h-[38px]"
              >
                clear
              </button>
              <button
                onClick={() => dispatch(toggleEditStake())}
                className="flex-1 bg-[#097C93] text-white py-2 min-w-20 w-[25%] h-[38px]"
              >
                Edit
              </button>

              <button
                onClick={reset}
                className="flex-1 bg-[#BD1828] text-white py-2 min-w-20 w-[25%] h-[38px]"
              >
                Reset
              </button>

              <button
                onClick={handleBetPlace}
                className="flex-1 bg-[#198754] text-white py-2 min-w-20 w-[25%] h-[38px]"
              >
                Submit
              </button>
            </div>
            {IsBetPlacing && <ComponentLoader />}
          </div>
        </>
      )}
      {/* My Bet Header */}
      <div className="bg-black text-white px-3 py-1 font-semibold mt-1">My Bet</div>
      <MyBets />
    </div>
  );
}
